// AI assistant — a floating chat dock (bottom-right, every page) powered by the
// user's own Anthropic key. Pages push "what's on screen" into AssistantProvider
// (selected stocks, current blend, KPIs) via useAssistantView; the dock sends
// that + the route + the question to POST /api/assistant/ask, which grounds the
// model in the live strategy data server-side. Read-only: it explains, never trades.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { renderMarkdown } from "./md.js";
import { api } from "./api.js";
import { useAccount } from "./account.jsx";
import { Icon } from "./components/Icon.jsx";

const ViewCtx = createContext({ view: {}, setView: () => {}, pending: null, ask: () => {}, clearPending: () => {} });

export function AssistantProvider({ children }) {
  const [view, setView] = useState({});
  const [pending, setPending] = useState(null);   // a question pushed by a page button (e.g. "Summarize")
  const seq = useRef(0);
  const ask = useCallback((q) => setPending({ q, id: ++seq.current }), []);
  const clearPending = useCallback(() => setPending(null), []);
  // Stabilize the context value identity (setView/ask/clearPending are already stable) so consumers
  // don't re-render on every provider render. Contents are unchanged.
  const value = useMemo(() => ({ view, setView, pending, ask, clearPending }), [view, pending]);
  return <ViewCtx.Provider value={value}>{children}</ViewCtx.Provider>;
}
export const useAssistantView = () => useContext(ViewCtx);
// Lightweight trigger for pages: const { ask } = useAssistant(); ask("Summarize…")
// opens the dock and sends the question with the page's registered view context.
export const useAssistant = () => useContext(ViewCtx);

// The per-page Help affordance. STATIC-FIRST (docs/design/HELP_AUDIT.md F1): it opens the
// relevant Help Center guide — always works, no Anthropic key required — instead of firing an
// AI question that dead-ends without a key. Each guide offers "Ask the assistant about this",
// and the ✦ dock gives account-scoped AI on every page, so no capability is lost. `doc` is the
// help slug for this page (default: the overview); `topic` is used only for the tooltip/label.
export function HelpButton({ topic, doc = "OVERVIEW" }) {
  return (
    <Link to={`/help/${doc}`} className="help-btn"
      title={topic ? `Open the guide for ${topic}` : "Open the guide for this page"}
      aria-label={topic ? `Help with ${topic}` : "Help with this page"}>
      <Icon name="info" size={13} /> Help
    </Link>
  );
}

// Pages call useReportView({...}) to register what the user is looking at. Stable
// JSON-keyed so it only updates the context when the payload actually changes.
export function useReportView(payload) {
  const { setView } = useContext(ViewCtx);
  const key = JSON.stringify(payload || {});
  useEffect(() => { setView(payload || {}); }, [key]);   // eslint-disable-line react-hooks/exhaustive-deps
}

const STARTERS = [
  "Which stock is my biggest loser and why?",
  "Is my current mix riskier than v4.1?",
  "Which strategy components are constructive vs destructive?",
];

export function AssistantDock() {
  const { view, pending, clearPending } = useContext(ViewCtx);
  const { broker, env, label, live } = useAccount();   // UX-1: scope the assistant to the SELECTED account
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [msgs, busy, open]);

  // A page pushed a question (e.g. the "Summarize my portfolio" button) — open
  // the dock and send it with the page's current view context.
  useEffect(() => {
    if (!pending) return;
    setOpen(true);
    send(pending.q);
    clearPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending?.id]);

  async function send(text) {
    const question = (text ?? q).trim();
    if (!question || busy) return;
    const history = msgs.slice(-6);
    setMsgs((m) => [...m, { role: "user", content: question }]);
    setQ(""); setBusy(true);
    const v = { ...view, route: window.location.pathname };
    try {
      const r = await api("assistant/ask", { method: "POST",
        body: JSON.stringify({ question, view: v, history, broker, env }) });   // UX-1: account-scoped
      setMsgs((m) => [...m, { role: "assistant", content: r.answer || "(no answer returned)" }]);
    } catch (e) {
      const msg = e.status === 400
        ? "I need your Anthropic API key first — add it in **[Settings → AI assistant](/settings)** (it stays encrypted on the server, never in the browser). Meanwhile, the **[Help & guides](/help)** explain every page — no key needed."
        : e.status === 502
          ? "Anthropic rejected the call — check your key is valid and has credit in **[Settings](/settings)**."
          : `Something went wrong (${e.status || "network"}).`;
      setMsgs((m) => [...m, { role: "assistant", content: msg }]);
    } finally { setBusy(false); }
  }

  return (
    <>
      <button className="asst-fab" onClick={() => setOpen((o) => !o)} aria-label="AI assistant" title="AI assistant">
        {open ? "✕" : "✦"}
      </button>
      {open && (
        <div className={"asst-panel" + (expanded ? " asst-panel--lg" : "")} role="dialog" aria-label="AI assistant">
          <div className="asst-head">
            <span className="asst-title">✦ Assistant</span>
            <span className={"asst-sub" + (live ? " asst-sub--live" : "")}
              title="The assistant answers only about this account">
              grounded in {label || `${broker} ${env}`}{live ? " · LIVE" : ""}</span>
            <div className="asst-head__actions">
              <button className="asst-iconbtn" onClick={() => setExpanded((e) => !e)}
                title={expanded ? "Shrink" : "Expand"} aria-label="Expand">{expanded ? "⤡" : "⤢"}</button>
              <button className="asst-iconbtn" onClick={() => setOpen(false)} title="Close" aria-label="Close">✕</button>
            </div>
          </div>
          <div className="asst-body" ref={bodyRef}>
            {msgs.length === 0 && (
              <div className="asst-empty">
                <p>Ask me about your KPIs, the stocks you've selected, correlations, or a strategy mix you're designing.</p>
                <p className="muted" style={{ fontSize: 11, marginTop: -4 }}>Scoped to your selected account — and I remember it between sessions.</p>
                {STARTERS.map((s) => (
                  <button key={s} className="asst-starter" onClick={() => send(s)}>{s}</button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => (m.role === "assistant"
              ? <div key={i} className="asst-msg asst-msg--assistant md md--chat"
                     dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content, { breaks: true }) }} />
              : <div key={i} className="asst-msg asst-msg--user">{m.content}</div>
            ))}
            {busy && <div className="asst-msg asst-msg--assistant asst-msg--busy">thinking…</div>}
          </div>
          <div className="asst-input">
            <input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about your data…" aria-label="Ask the assistant" />
            <button className="btn" onClick={() => send()} disabled={busy || !q.trim()}>Send</button>
          </div>
        </div>
      )}
    </>
  );
}
